import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiCheckCircle,
  FiAlertTriangle,
  FiRefreshCcw,
  FiArrowLeft 
} from "react-icons/fi";
import "./PayrollClientDetail.css";
import { useNavigate } from "react-router-dom";

const PayrollClientDetail = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const { processId } = useParams();

  const API =
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000";

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingDeductions, setUploadingDeductions] = useState(false);
  const [, setImportResult] = useState(null);
  const [selectedReportFile, setSelectedReportFile] = useState(null);
  const [uploadingReport, setUploadingReport] = useState(false);
  const [, setReportResult] = useState(null);
  const [comparingPayroll, setComparingPayroll] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [activeMembersData, setActiveMembersData] = useState(null);
  const [withdrawalsData, setWithdrawalsData] = useState(null);
  const [, setLoadingWithdrawals] = useState(false);
  const [adjustmentsData, setAdjustmentsData] = useState(null);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);
  const [adjustmentValues, setAdjustmentValues] = useState({});
  const [loadingActiveMembers, setLoadingActiveMembers] = useState(false);
  const [payrollProcess, setPayrollProcess] = useState(null);
  const [erpPreview, setErpPreview] = useState([]);
  const [, setLoadingErpPreview] = useState(false);
  const [erpSummary, setErpSummary] = useState(null);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [adjustmentConcepts, setAdjustmentConcepts] = useState([]);
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const navigate = useNavigate();

  const initializeView = async () => {
    try {

      await Promise.all([
        loadProcess(),
        loadWithdrawals(),
        loadAdjustments(),
        loadActiveMembers(),
        loadErpPreview(),
      ]);

    } catch (error) {
      console.error(
        "Error inicializando vista:",
        error
      );
    }
  };

  useEffect(() => {
    initializeView();
  }, [processId]);


  const handleUploadDeductions = async () => {
    try {
      if (!selectedFile) {
        toast.warning(
          "Debes seleccionar un archivo."
        );
        return;
      }

      setUploadingDeductions(true);

      const formData = new FormData();

      formData.append(
        "file",
        selectedFile
      );

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.post(
        `${API}/api/payroll/processes/${processId}/upload-deductions`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      setImportResult(
        response.data.data
      );

      toast.success(
        "Deducciones importadas correctamente."
      );

      setSelectedFile(null);
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
        "Error importando deducciones."
      );
    } finally {
      setUploadingDeductions(false);
    }
  };

  const handleUploadReport = async () => {
    try {
      if (!selectedReportFile) {
        toast.warning(
          "Debes seleccionar un archivo."
        );
        return;
      }

      setUploadingReport(true);

      const formData = new FormData();

      formData.append(
        "file",
        selectedReportFile
      );

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.post(
        `${API}/api/payroll/processes/${processId}/upload-report`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      setReportResult(
        response.data.data
      );

      toast.success(
        "Reporte importado correctamente."
      );

      setSelectedReportFile(null);
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
        "Error importando reporte."
      );
    } finally {
      setUploadingReport(false);
    }
  };

  const handleComparePayroll = async () => {
    try {
      setComparingPayroll(true);

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.post(
        `${API}/api/payroll/processes/${processId}/compare`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setComparisonResult(
        response.data.data
      );

      setWithdrawalsData(null);
      setAdjustmentsData(null);
      setActiveMembersData(null);
      setErpPreview([]);

      await Promise.all([
        loadProcess(),
        loadWithdrawals(),
        loadAdjustments(),
        loadActiveMembers(),
        loadErpPreview(),
      ]);

      toast.success(
        response.data.message ||
        "Comparativo ejecutado correctamente."
      );
    } catch (error) {
      console.error(
        "Error ejecutando el comparativo:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "No fue posible ejecutar el comparativo."
      );
    } finally {
      setComparingPayroll(false);
    }
  };

  const getComparisonStatusLabel = (status) => {
    const statusLabels = {
      MATCH: "Coincide",
      DIFFERENCE: "Diferencia",
      ONLY_DEDUCTION: "Solo en deducciones",
      ONLY_REPORT: "Solo en reporte",
      WITHDRAWAL: "Retiro",
      ADJUSTMENT: "Ajuste",
    };

    return statusLabels[status] || status;
  };

  const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const openAdjustmentModal = (person) => {

    setSelectedAdjustment(person);

    setAdjustmentConcepts(
      person.concepts.map((concept) => ({
        ...concept,
      }))
    );

    setAdjustmentModalOpen(true);
  };

  const getCurrentBalance = () => {
    if (!selectedAdjustment) {
      return 0;
    }

    const distributedTotal =
      adjustmentConcepts
        .filter(
          (concept) =>
            !concept.excluded
        )
        .reduce(
          (total, concept) =>
            total +
            Number(
              concept.newValue || 0
            ),
          0
        );

    return Number(
      (
        Number(
          selectedAdjustment.reportedTotal || 0
        ) -
        distributedTotal
      ).toFixed(2)
    );
  };

  const updateConceptValue = (
    recordId,
    inputValue
  ) => {
    const numericValue = Math.max(
      Number(inputValue || 0),
      0
    );

    const currentConcept =
      adjustmentConcepts.find(
        (concept) =>
          String(concept.recordId) ===
          String(recordId)
      );

    if (
      !currentConcept ||
      currentConcept.excluded
    ) {
      return;
    }

    const totalFromOtherConcepts =
      adjustmentConcepts
        .filter(
          (concept) =>
            String(concept.recordId) !==
            String(recordId) &&
            !concept.excluded
        )
        .reduce(
          (total, concept) =>
            total +
            Number(
              concept.newValue || 0
            ),
          0
        );

    const maximumAllowed = Math.max(
      Number(
        selectedAdjustment?.reportedTotal || 0
      ) -
      totalFromOtherConcepts,
      0
    );

    const validatedValue = Math.min(
      numericValue,
      maximumAllowed
    );

    setAdjustmentConcepts(
      (currentConcepts) =>
        currentConcepts.map(
          (concept) =>
            String(concept.recordId) ===
              String(recordId)
              ? {
                ...concept,
                newValue:
                  validatedValue,
              }
              : concept
        )
    );
  };

  const toggleConceptExclusion = (
    recordId
  ) => {
    setAdjustmentConcepts(
      (currentConcepts) => {
        const selectedConcept =
          currentConcepts.find(
            (concept) =>
              String(concept.recordId) ===
              String(recordId)
          );

        if (!selectedConcept) {
          return currentConcepts;
        }

        const willBeExcluded =
          !selectedConcept.excluded;

        if (!willBeExcluded) {
          const currentDistributedTotal =
            currentConcepts
              .filter(
                (concept) =>
                  !concept.excluded &&
                  String(
                    concept.recordId
                  ) !== String(recordId)
              )
              .reduce(
                (total, concept) =>
                  total +
                  Number(
                    concept.newValue || 0
                  ),
                0
              );

          const totalAfterActivation =
            currentDistributedTotal +
            Number(
              selectedConcept.newValue || 0
            );

          const reportedTotal =
            Number(
              selectedAdjustment
                ?.reportedTotal || 0
            );

          if (
            totalAfterActivation >
            reportedTotal
          ) {
            toast.warning(
              "No puedes activar este concepto porque los valores superarían el total reportado."
            );

            return currentConcepts;
          }
        }

        return currentConcepts.map(
          (concept) =>
            String(concept.recordId) ===
              String(recordId)
              ? {
                ...concept,
                excluded:
                  willBeExcluded,
              }
              : concept
        );
      }
    );
  };

  const saveAdjustmentPerson = async () => {
    if (
      savingAdjustment ||
      !selectedAdjustment
    ) {
      return;
    }

    const currentBalance =
      getCurrentBalance();

    if (currentBalance < 0) {
      toast.warning(
        "Los valores distribuidos no pueden superar el total reportado.",
        {
          toastId:
            "payroll-adjustment-overflow",
        }
      );

      return;
    }

    try {
      setSavingAdjustment(true);

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const documentNumber =
        selectedAdjustment.documentNumber;

      const conceptsToSave =
        adjustmentConcepts.map(
          (concept) => {
            const newValue = Number(
              concept.newValue || 0
            );

            return {
              ...concept,
              newValue,
              excluded:
                newValue === 0
                  ? true
                  : Boolean(
                    concept.excluded
                  ),
            };
          }
        );

      setAdjustmentConcepts(
        conceptsToSave
      );

      const response =
        await axios.post(
          `${API}/api/payroll/processes/${processId}/adjustments/save-person`,
          {
            documentNumber,
            concepts:
              conceptsToSave,
          },
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      setComparisonResult(
        (currentComparison) => {
          if (
            !currentComparison ||
            !Array.isArray(
              currentComparison.results
            )
          ) {
            return currentComparison;
          }

          const updatedResults =
            currentComparison.results.map(
              (item) => {
                if (
                  String(
                    item.documentNumber
                  ) !==
                  String(documentNumber)
                ) {
                  return item;
                }

                return {
                  ...item,
                  deductionTotal:
                    response.data
                      .deductionTotal,
                  reportedTotal:
                    response.data
                      .reportedTotal,
                  difference:
                    response.data
                      .difference,
                  status:
                    response.data.status,
                };
              }
            );

          return {
            ...currentComparison,

            results:
              updatedResults,

            summary: {
              ...currentComparison.summary,

              matches:
                updatedResults.filter(
                  (item) =>
                    item.status ===
                    "MATCH"
                ).length,

              differences:
                updatedResults.filter(
                  (item) =>
                    item.status ===
                    "DIFFERENCE"
                ).length,
            },
          };
        }
      );

      toast.success(
        response.data.message ||
        "Ajuste guardado correctamente.",
        {
          toastId:
            "payroll-adjustment-saved",
        }
      );

      setAdjustmentModalOpen(false);
      setSelectedAdjustment(null);
      setAdjustmentConcepts([]);

      await Promise.all([
        loadAdjustments(),
        loadErpPreview(),
      ]);
    } catch (error) {
      console.error(
        "Error guardando ajuste:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "No fue posible guardar el ajuste.",
        {
          toastId:
            "payroll-adjustment-error",
        }
      );
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleGenerateErp = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.post(
        `${API}/api/payroll/processes/${processId}/generate-erp`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },

          responseType: "blob",
        }
      );

      const contentDisposition =
        response.headers[
        "content-disposition"
        ];

      let fileName =
        "ERP_NOMINA.xlsx";

      const fileNameMatch =
        contentDisposition?.match(
          /filename="?([^"]+)"?/
        );

      if (fileNameMatch?.[1]) {
        fileName =
          fileNameMatch[1];
      }

      const fileUrl =
        window.URL.createObjectURL(
          new Blob(
            [response.data],
            {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }
          )
        );

      const downloadLink =
        document.createElement("a");

      downloadLink.href =
        fileUrl;

      downloadLink.setAttribute(
        "download",
        fileName
      );

      document.body.appendChild(
        downloadLink
      );

      downloadLink.click();

      downloadLink.remove();

      window.URL.revokeObjectURL(
        fileUrl
      );

      toast.success(
        "Archivo ERP generado correctamente."
      );
    } catch (error) {
      console.error(
        "Error generando ERP:",
        error
      );

      let errorMessage =
        "No fue posible generar el archivo ERP.";

      if (
        error?.response?.data instanceof
        Blob
      ) {
        try {
          const errorText =
            await error.response.data.text();

          const errorData =
            JSON.parse(errorText);

          errorMessage =
            errorData.message ||
            errorMessage;
        } catch (
        blobError
        ) {
          console.error(
            "No fue posible leer el error del servidor:",
            blobError
          );
        }
      }

      toast.error(
        errorMessage
      );
    }
  };

  const loadActiveMembers = async () => {
    try {
      setLoadingActiveMembers(true);

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.get(
        `${API}/api/payroll/processes/${processId}/active-members`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setActiveMembersData(
        response.data.data
      );

    } catch (error) {

      console.error(
        "Error consultando asociados activos:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "No fue posible consultar los asociados activos."
      );

    } finally {

      setLoadingActiveMembers(false);

    }
  };

  const loadProcess = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.get(
        `${API}/api/payroll/processes/${processId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPayrollProcess(
        response.data.data
      );

      if (
        response.data.data?.summaries
          ?.comparisonResult
      ) {

        setComparisonResult(
          response.data.data.summaries
            .comparisonResult
        );

      }

    } catch (error) {
      console.error(
        "Error cargando proceso:",
        error
      );
    }
  };

  const loadWithdrawals = async () => {
    try {
      setLoadingWithdrawals(true);

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.get(
        `${API}/api/payroll/processes/${processId}/withdrawals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setWithdrawalsData(
        response.data.data
      );
    } catch (error) {
      console.error(
        "Error cargando retiros:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "No fue posible consultar los retiros."
      );
    } finally {
      setLoadingWithdrawals(false);
    }
  };

  const loadAdjustments = async () => {
    try {
      setLoadingAdjustments(true);

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.get(
        `${API}/api/payroll/processes/${processId}/adjustments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAdjustmentsData(
        response.data.data
      );

      const values = {};

      response.data.data.records?.forEach(
        (record) => {
          values[record.recordId] =
            record.currentValue;
        }
      );

      setAdjustmentValues(values);
    } catch (error) {
      console.error(
        "Error cargando ajustes:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "No fue posible consultar los ajustes."
      );
    } finally {
      setLoadingAdjustments(false);
    }
  };

  const loadErpPreview = async () => {
    try {
      setLoadingErpPreview(true);

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const response = await axios.get(
        `${API}/api/payroll/processes/${processId}/erp-preview`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setErpPreview(
        response.data.data || []
      );

      setErpSummary(
        response.data.summary || null
      );

    } catch (error) {
      console.error(
        "Error cargando preview ERP:",
        error
      );

      console.warn(
        error?.response?.data?.message
      );
    } finally {
      setLoadingErpPreview(false);
    }
  };

  const formatName = (name = "") =>
    name
      .toLowerCase()
      .split(" ")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() +
          word.slice(1)
      )
      .join(" ");

  const openAdjustmentFromDifference =
    (item) => {

      const person =
        adjustmentsData?.records?.find(
          (record) =>
            record.documentNumber ===
            item.documentNumber
        );

      if (!person) {

        toast.warning(
          "La persona no se encuentra en ajustes."
        );

        return;
      }

      openAdjustmentModal(
        person
      );
    };

  const getPayrollStatusLabel = (
    status
  ) => {

    const labels = {

      CREATED:
        "Creado",

      DEDUCTIONS_UPLOADED:
        "Deducciones cargadas",

      REPORT_UPLOADED:
        "Reporte cargado",

      PROCESSING:
        "Procesando",

      WITH_DIFFERENCES:
        "Con diferencias",

      RECONCILED:
        "Conciliado",

      EXPORTED:
        "ERP generado",

      ERROR:
        "Error"

    };

    return labels[status] || status;

  };

  return (
    <div className="payroll-detail-container">

      <div className="payroll-detail-header">
        <button
          className="payroll-back-button"
          onClick={() => navigate("/payroll")}
          title="Volver a Nómina"
        >
          <FiArrowLeft size={20} />
        </button>

        <div>
          <h1>
            Nómina {payrollProcess?.company || "-"}
          </h1>

          <p>
            Periodo de validación:{" "}
            {payrollProcess?.period?.month}/
            {payrollProcess?.period?.year}
          </p>
        </div>
      </div>

      <div className="payroll-detail-tabs">

        <button
          className={`payroll-detail-tab ${activeTab === "summary" ? "active" : ""
            }`}
          onClick={() => setActiveTab("summary")}
        >
          Resumen
        </button>

        <button
          className={`payroll-detail-tab ${activeTab === "differences" ? "active" : ""
            }`}
          onClick={() => setActiveTab("differences")}
        >
          Comparativo
        </button>

        <button
          className={`payroll-detail-tab ${activeTab === "adjustments" ? "active" : ""
            }`}
          onClick={() => setActiveTab("adjustments")}
        >
          Ajustes
        </button>

        <button
          className={`payroll-detail-tab ${activeTab === "withdrawals" ? "active" : ""
            }`}
          onClick={() => setActiveTab("withdrawals")}
        >
          Retiros
        </button>

        <button
          className={`payroll-detail-tab ${activeTab === "activeMembers" ? "active" : ""
            }`}
          onClick={() => setActiveTab("activeMembers")}
        >
          Activos
        </button>

        <button
          className={`payroll-detail-tab ${activeTab === "erp" ? "active" : ""
            }`}
          onClick={() => setActiveTab("erp")}
        >
          ERP
        </button>

      </div>

      {activeTab === "summary" && (
        <>
          <div className="payroll-summary-cards">

            <div className="payroll-summary-card">
              <span>Deducciones</span>
              <strong>
                {(payrollProcess?.summaries?.deductions?.records || 0).toLocaleString("es-CO")}
              </strong>
              <small>
                Registros enviados
              </small>
            </div>

            <div className="payroll-summary-card">
              <span>Reporte</span>
              <strong>
                {(payrollProcess?.summaries?.report?.records || 0).toLocaleString("es-CO")}
              </strong>
              <small>
                Registros recibidos
              </small>
            </div>

            <div className="payroll-summary-card">
              <span>Diferencias</span>
              <strong>
                {(payrollProcess?.summaries?.comparison?.differences || 0).toLocaleString("es-CO")}
              </strong>
              <small>
                Pendientes
              </small>
            </div>

            <div className="payroll-summary-card">
              <span>ERP Final</span>
              <strong>
                {formatCurrency(
                  payrollProcess?.summaries?.erp?.totalValue || 0
                )}
              </strong>
              <small>
                Valor conciliado
              </small>
            </div>
          </div>

          <div className="payroll-summary-files">

            <div className="payroll-file-card">

              <h3>
                Deducciones
              </h3>

              <div className="payroll-file-date">

                Última carga: {" "}

                {
                  payrollProcess?.files?.deductions?.uploadedAt
                    ? new Date(
                      payrollProcess.files.deductions.uploadedAt
                    ).toLocaleString("es-CO")
                    : "Sin cargar"
                }

              </div>

              <div className="payroll-file-metrics">

                <div>
                  <span>Registros</span>
                  <strong>
                    {(payrollProcess?.summaries?.deductions?.records || 0).toLocaleString("es-CO")}
                  </strong>
                </div>

                <div>
                  <span>Empleados</span>
                  <strong>
                    {(payrollProcess?.summaries?.deductions?.employees || 0).toLocaleString("es-CO")}
                  </strong>
                </div>

                <div>
                  <span>Valor</span>
                  <strong>
                    {formatCurrency(
                      payrollProcess?.summaries?.deductions?.totalValue || 0
                    )}
                  </strong>
                </div>

                <div>
                  <span>Archivo</span>
                  <div className="payroll-file-name">
                    {
                      payrollProcess?.files
                        ?.deductions
                        ?.originalName ||
                      "-"
                    }
                  </div>
                </div>

              </div>

              <input
                id="upload-deductions"
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                onChange={(e) =>
                  setSelectedFile(
                    e.target.files?.[0]
                  )
                }
              />

              <button
                className="payroll-secondary-button"
                onClick={() => {

                  if (!selectedFile) {
                    document
                      .getElementById(
                        "upload-deductions"
                      )
                      ?.click();

                    return;
                  }

                  handleUploadDeductions();

                }}
                disabled={
                  uploadingDeductions
                }
              >

                {
                  uploadingDeductions
                    ? "Importando..."
                    : selectedFile
                      ? "Confirmar"
                      : "Importar Deducciones"
                }

              </button>

            </div>

            <div className="payroll-file-card">

              <h3>
                Reporte
              </h3>

              <div className="payroll-file-date">

                Última carga: {" "}

                {
                  payrollProcess?.files?.report?.uploadedAt
                    ? new Date(
                      payrollProcess.files.report.uploadedAt
                    ).toLocaleString("es-CO")
                    : "Sin cargar"
                }

              </div>

              <div className="payroll-file-metrics">

                <div>
                  <span>Registros</span>
                  <strong>
                    {(payrollProcess?.summaries?.report?.records || 0).toLocaleString("es-CO")}
                  </strong>
                </div>

                <div>
                  <span>Empleados</span>
                  <strong>
                    {(payrollProcess?.summaries?.report?.employees || 0).toLocaleString("es-CO")}
                  </strong>
                </div>

                <div>
                  <span>Valor</span>
                  <strong>
                    {formatCurrency(
                      payrollProcess?.summaries?.report?.totalValue || 0
                    )}
                  </strong>
                </div>

                <div>
                  <span>Archivo</span>
                  <div className="payroll-file-name">
                    {
                      payrollProcess?.files
                        ?.report
                        ?.originalName ||
                      "-"
                    }
                  </div>
                </div>

              </div>

              <input
                id="upload-report"
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                onChange={(e) =>
                  setSelectedReportFile(
                    e.target.files?.[0]
                  )
                }
              />

              <button
                className="payroll-secondary-button"
                onClick={() => {

                  if (!selectedReportFile) {

                    document
                      .getElementById(
                        "upload-report"
                      )
                      ?.click();

                    return;
                  }

                  handleUploadReport();

                }}
                disabled={
                  uploadingReport
                }
              >

                {
                  uploadingReport
                    ? "Importando..."
                    : selectedReportFile
                      ? "Confirmar"
                      : "Importar Reporte"
                }

              </button>

            </div>

          </div>

          <div className="payroll-process-health">

            <h3>
              Estado del proceso
            </h3>

            <div className="payroll-health-list">

              <div>

                {
                  payrollProcess?.files?.deductions?.uploadedAt
                    ? <FiCheckCircle color="#10b981" />
                    : <FiAlertTriangle color="#f59e0b" />
                }

                {
                  payrollProcess?.files?.deductions?.uploadedAt
                    ? "Deducciones cargadas"
                    : "Pendiente cargar deducciones"
                }

              </div>

              <div>

                {
                  payrollProcess?.files?.report?.uploadedAt
                    ? <FiCheckCircle color="#10b981" />
                    : <FiAlertTriangle color="#f59e0b" />
                }

                {
                  payrollProcess?.files?.report?.uploadedAt
                    ? "Reporte cargado"
                    : "Pendiente cargar reporte"
                }

              </div>

              <div>

                {
                  (payrollProcess?.summaries?.comparison?.differences || 0) > 0
                    ? <FiAlertTriangle color="#f59e0b" />
                    : <FiCheckCircle color="#10b981" />
                }

                {
                  (payrollProcess?.summaries?.comparison?.differences || 0) > 0
                    ? `${(
                      payrollProcess?.summaries?.comparison?.differences || 0
                    ).toLocaleString("es-CO")} diferencias`
                    : "Sin diferencias"
                }

              </div>

              <div>

                {
                  (withdrawalsData?.summary?.withdrawals || 0) > 0
                    ? <FiRefreshCcw color="#ef4444" />
                    : <FiCheckCircle color="#10b981" />
                }

                {
                  (withdrawalsData?.summary?.withdrawals || 0) > 0
                    ? `${withdrawalsData.summary.withdrawals} retiros`
                    : "Sin retiros"
                }

              </div>

            </div>

          </div>
        </>
      )}

      {activeTab === "differences" && (
        <div className="payroll-differences-section">
          <div className="payroll-detail-panel">
            <div className="payroll-comparison-header">
              <div>
                <h3>Comparativo de nómina</h3>

                <p>
                  Compara las deducciones enviadas con el reporte recibido
                  del cliente, consolidando los valores por identificación.
                </p>
              </div>

              <button
                type="button"
                className="pay-btn-primary"
                onClick={handleComparePayroll}
                disabled={comparingPayroll}
              >
                {comparingPayroll
                  ? "Comparando..."
                  : comparisonResult
                    ? "Ejecutar nuevamente"
                    : "Ejecutar comparativo"}
              </button>
            </div>

            {!comparisonResult && !comparingPayroll && (
              <div className="payroll-comparison-empty">
                <div className="payroll-comparison-empty-icon">
                  ↔
                </div>

                <h4>Aún no se ha ejecutado el comparativo</h4>

                <p>
                  Verifica que las deducciones y el reporte estén cargados
                  antes de iniciar el proceso.
                </p>
              </div>
            )}

            {comparingPayroll && (
              <div className="payroll-comparison-loading">
                <div className="payroll-comparison-spinner" />

                <div>
                  <strong>Procesando información</strong>

                  <p>
                    Estamos consolidando y comparando los valores por
                    identificación.
                  </p>
                </div>
              </div>
            )}
          </div>

          {comparisonResult && !comparingPayroll && (
            <>
              <div className="payroll-comparison-metrics">
                <div className="payroll-comparison-metric">
                  <span>Personas comparadas</span>

                  <strong>
                    {(comparisonResult.summary?.totalEmployees || 0).toLocaleString("es-CO")}
                  </strong>

                  <small>
                    Total de identificaciones encontradas
                  </small>
                </div>

                <div className="payroll-comparison-metric">
                  <span>Coincidencias</span>

                  <strong>
                    {(comparisonResult.summary?.matches || 0).toLocaleString("es-CO")}
                  </strong>

                  <small>
                    Sin diferencias de valor
                  </small>
                </div>

                <div className="payroll-comparison-metric">
                  <span>Diferencias</span>

                  <strong>
                    {(comparisonResult.summary?.differences || 0).toLocaleString("es-CO")}
                  </strong>

                  <small>
                    Requieren validación o ajuste
                  </small>
                </div>

                <div className="payroll-comparison-metric">
                  <span>Solo en deducciones</span>

                  <strong>
                    {(comparisonResult.summary?.onlyInDeductions || 0).toLocaleString("es-CO")}
                  </strong>

                  <small>
                    No aparecen en el reporte
                  </small>
                </div>

                <div className="payroll-comparison-metric">
                  <span>Solo en reporte</span>

                  <strong>
                    {(comparisonResult.summary?.onlyInReport || 0).toLocaleString("es-CO")}
                  </strong>

                  <small>
                    No aparecen en deducciones
                  </small>
                </div>
              </div>

              <div className="payroll-comparison-totals">
                <div>
                  <span>Total enviado</span>

                  <strong>
                    {formatCurrency(
                      comparisonResult.totals?.deductions
                    )}
                  </strong>
                </div>

                <div>
                  <span>Total reportado</span>

                  <strong>
                    {formatCurrency(
                      comparisonResult.totals?.report
                    )}
                  </strong>
                </div>

                <div>
                  <span>Diferencia total</span>

                  <strong>
                    {formatCurrency(
                      comparisonResult.totals?.difference
                    )}
                  </strong>
                </div>
              </div>

              <div className="payroll-detail-panel payroll-comparison-results">
                <div className="payroll-results-header">
                  <div>
                    <h3>Novedades encontradas</h3>

                    <p>
                      Se muestran únicamente las personas que requieren
                      revisión.
                    </p>
                  </div>

                  <span className="payroll-results-count">
                    {comparisonResult.results?.filter(
                      (item) => item.status !== "MATCH"
                    ).length || 0}{" "}
                    registros
                  </span>
                </div>

                <div className="payroll-detail-table-container">
                  <table className="payroll-detail-table">
                    <thead>
                      <tr>
                        <th>Identificación</th>
                        <th>Nombre</th>
                        <th>Deducción</th>
                        <th>Reportado</th>
                        <th>Diferencia</th>
                        <th>Estado</th>
                        <th>Acción</th>
                      </tr>
                    </thead>

                    <tbody>
                      {comparisonResult.results
                        ?.filter((item) => item.status !== "MATCH")
                        .sort((a, b) => {
                          const statusOrder = {
                            DIFFERENCE: 1,
                            ONLY_DEDUCTION: 2,
                            ONLY_REPORT: 3,
                          };

                          const statusComparison =
                            (statusOrder[a.status] || 99) -
                            (statusOrder[b.status] || 99);

                          if (statusComparison !== 0) {
                            return statusComparison;
                          }

                          return (a.difference || 0) - (b.difference || 0);
                        })
                        .map((item) => (
                          <tr key={item.documentNumber}>
                            <td>
                              {item.documentNumber}
                            </td>

                            <td>
                              {formatName(item.employeeName || "Sin nombre")}
                            </td>

                            <td>
                              {formatCurrency(
                                item.deductionTotal
                              )}
                            </td>

                            <td>
                              {formatCurrency(
                                item.reportedTotal
                              )}
                            </td>

                            <td>
                              <strong>
                                {formatCurrency(
                                  item.difference
                                )}
                              </strong>
                            </td>

                            <td>
                              <span className="payroll-comparison-status">
                                {getComparisonStatusLabel(
                                  item.status
                                )}
                              </span>
                            </td>

                            <td>
                              {item.status === "DIFFERENCE" && (
                                <button
                                  type="button"
                                  className="payroll-table-action-button"
                                  onClick={() =>
                                    openAdjustmentFromDifference(
                                      item
                                    )
                                  }
                                >
                                  Ajustar
                                </button>
                              )}

                              {item.status === "ONLY_DEDUCTION" && (
                                <button
                                  type="button"
                                  className="payroll-table-action-button"
                                  onClick={() => {
                                    setActiveTab("withdrawals");
                                  }}
                                >
                                  Validar
                                </button>
                              )}

                              {item.status === "ONLY_REPORT" && (
                                <span className="payroll-no-action">
                                  Sin acción
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {comparisonResult.results?.filter(
                  (item) => item.status !== "MATCH"
                ).length === 0 && (
                    <div className="payroll-comparison-success">
                      <div className="payroll-comparison-success-icon">
                        ✓
                      </div>

                      <div>
                        <strong>Proceso conciliado</strong>

                        <p>
                          Todas las personas coinciden entre las deducciones
                          y el reporte del cliente.
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "withdrawals" && (
        <div className="payroll-withdrawals-section">

          <div className="payroll-detail-panel">

            <div className="payroll-withdrawals-header">

              <div>
                <h3>Retiros Detectados</h3>

                <p>
                  Asociados identificados con paz y salvo que
                  requieren generación del archivo de retiro.
                </p>
              </div>

              <button
                type="button"
                className="pay-btn-primary"
              >
                Exportar Retiros
              </button>

            </div>

          </div>

          <div className="payroll-withdrawals-metrics">

            <div className="payroll-withdrawal-card">
              <span>Retiros detectados</span>
              <strong>
                {(withdrawalsData?.summary?.withdrawals || 0).toLocaleString("es-CO")}
              </strong>
            </div>

            <div className="payroll-withdrawal-card">
              <span>Saldo total</span>
              <strong>
                {formatCurrency(
                  withdrawalsData?.summary?.withdrawalBalance
                )}
              </strong>
            </div>

            <div className="payroll-withdrawal-card">
              <span>Procesados</span>
              <strong>
                {(withdrawalsData?.summary?.reviewed || 0).toLocaleString("es-CO")}
              </strong>
            </div>

            <div className="payroll-withdrawal-card">
              <span>Pendientes</span>
              <strong>
                {(withdrawalsData?.summary?.adjustments || 0).toLocaleString("es-CO")}
              </strong>
            </div>

          </div>

          <div className="payroll-detail-panel">

            <div className="payroll-results-header">

              <div>
                <h3>Detalle de Retiros</h3>

                <p>
                  Personas identificadas con paz y salvo.
                </p>
              </div>

            </div>

            <div className="payroll-detail-table-container">

              <table className="payroll-detail-table">

                <thead>
                  <tr>
                    <th>Identificación</th>
                    <th>Nombre</th>
                    <th>Fecha Paz y Salvo</th>
                    <th>Saldo Paz y Salvo</th>
                    <th>Estado</th>
                  </tr>
                </thead>

                <tbody>

                  {withdrawalsData?.withdrawals?.map(
                    (withdrawal) => (
                      <tr
                        key={withdrawal.documentNumber}
                      >
                        <td>
                          {withdrawal.documentNumber}
                        </td>

                        <td>
                          {formatName(withdrawal.employeeName || "Sin nombre")}
                        </td>

                        <td>
                          {withdrawal.clearanceDate
                            ? new Date(
                              withdrawal.clearanceDate
                            ).toLocaleDateString("es-CO")
                            : "-"}
                        </td>

                        <td>
                          {formatCurrency(
                            withdrawal.clearanceBalance
                          )}
                        </td>

                        <td>
                          <span className="payroll-retirement-badge">
                            Retiro
                          </span>
                        </td>
                      </tr>
                    )
                  )}

                </tbody>

              </table>

            </div>

          </div>

        </div>
      )}

      {activeTab === "adjustments" && (
        <div className="payroll-adjustments-section">

          <div className="payroll-detail-panel">

            <div className="payroll-adjustments-header">

              <div>
                <h3>Ajustes de Nómina</h3>

                <p>
                  Personas que presentan diferencias y no poseen
                  paz y salvo registrado.
                </p>
              </div>

            </div>

          </div>

          <div className="payroll-adjustments-metrics">

            <div className="payroll-adjustment-card">
              <span>Personas</span>

              <strong>
                {(adjustmentsData?.totalPeople || 0).toLocaleString("es-CO")}
              </strong>

              <small>
                Con diferencias
              </small>
            </div>

            <div className="payroll-adjustment-card">
              <span>Valor Pendiente</span>

              <strong>
                {formatCurrency(

                  adjustmentsData?.records?.reduce(
                    (sum, item) =>
                      sum +
                      Math.abs(
                        item.balance || 0
                      ),
                    0
                  ) || 0

                )}
              </strong>

              <small>
                Por conciliar
              </small>
            </div>

            <div className="payroll-adjustment-card">
              <span>Conciliadas</span>

              <strong>
                {
                  (
                    adjustmentsData?.records?.filter(
                      (item) =>
                        item.status === "MATCH"
                    ).length || 0
                  ).toLocaleString("es-CO")
                }
              </strong>

              <small>
                Sin diferencias
              </small>
            </div>

            <div className="payroll-adjustment-card">
              <span>Pendientes</span>

              <strong>
                {
                  (
                    adjustmentsData?.records?.filter(
                      (item) =>
                        item.status === "PENDING"
                    ).length || 0
                  ).toLocaleString("es-CO")
                }
              </strong>

              <small>
                Requieren revisión
              </small>
            </div>

          </div>

          <div className="payroll-detail-panel">

            <div className="payroll-results-header">

              <div>
                <h3>Detalle de Ajustes</h3>

                <p>
                  Ajusta el valor enviado para igualarlo con el valor
                  efectivamente descontado.
                </p>
              </div>

            </div>

            <div className="payroll-detail-table-container">

              {loadingAdjustments && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  Consultando ajustes...
                </div>
              )}

              <table className="payroll-detail-table">

                <thead>
                  <tr>
                    <th>Identificación</th>
                    <th>Nombre</th>
                    <th>Deducción</th>
                    <th>Reportado</th>
                    <th>Saldo</th>
                    <th>Conceptos</th>
                    <th>Estado</th>
                    <th>Acción</th>
                  </tr>
                </thead>

                <tbody>
                  {adjustmentsData?.records
                    ?.sort(
                      (a, b) =>
                        (b.balance || 0) -
                        (a.balance || 0)
                    )
                    .map((person) => (
                      <tr key={person.documentNumber}>
                        <td>{person.documentNumber}</td>

                        <td>
                          {formatName(
                            person.employeeName ||
                            "Sin nombre"
                          )}
                        </td>

                        <td>
                          {formatCurrency(
                            person.sentTotal
                          )}
                        </td>

                        <td>
                          {formatCurrency(
                            person.reportedTotal
                          )}
                        </td>

                        <td>
                          <strong
                            style={{
                              color:
                                person.balance === 0
                                  ? "#16a34a"
                                  : "#dc2626",
                            }}
                          >
                            {formatCurrency(
                              person.balance
                            )}
                          </strong>
                        </td>

                        <td>
                          {person.concepts.length}
                        </td>

                        <td>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            {person.status === "MATCH" ? (
                              <>
                                <FiCheckCircle color="#999999" />
                                Cuadrado
                              </>
                            ) : (
                              <>
                                <FiAlertTriangle color="#999999" />
                                Pendiente
                              </>
                            )}
                          </span>
                        </td>

                        <td>
                          <button
                            className="payroll-table-action-button"
                            onClick={() =>
                              openAdjustmentModal(
                                person
                              )
                            }
                          >
                            Ajustar
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>

              </table>

            </div>

          </div>

        </div>
      )}

      {activeTab === "erp" && (
        <div className="payroll-erp-section">

          <div className="payroll-detail-panel">

            <div className="payroll-erp-header">

              <div>
                <h3>Generación Archivo ERP</h3>

                <p>
                  Genera el archivo final para cargue al ERP con todos los
                  registros conciliados correctamente.
                </p>
              </div>

              <button
                type="button"
                className="pay-btn-primary"
                onClick={handleGenerateErp}
              >
                Generar Archivo ERP
              </button>

            </div>

          </div>

          <div className="payroll-erp-metrics">

            <div className="payroll-erp-card">
              <span>Registros ERP</span>
              <strong>
                {(erpSummary?.records || 0).toLocaleString("es-CO")}
              </strong>
            </div>

            <div className="payroll-erp-card">
              <span>Personas</span>
              <strong>
                {(erpSummary?.employees || 0).toLocaleString("es-CO")}
              </strong>
            </div>

            <div className="payroll-erp-card">
              <span>Valor Total</span>
              <strong>
                {
                  formatCurrency(
                    erpSummary?.totalValue || 0
                  )
                }
              </strong>
            </div>

            <div className="payroll-erp-card">
              <span>Estado</span>

              <div className="payroll-status-badge">
                {getPayrollStatusLabel(
                  payrollProcess?.status
                )}
              </div>
            </div>

          </div>

          <div className="payroll-detail-panel">

            <div className="payroll-results-header">

              <div>
                <h3>Vista Previa</h3>

                <p>
                  Primeros 10 registros que serán incluidos en el archivo
                  final de exportación.
                </p>
              </div>

            </div>

            <div className="payroll-detail-table-container">

              <table className="payroll-detail-table">

                <thead>
                  <tr>
                    <th>Identificación</th>
                    <th>Número de Producto</th>
                    <th>Valor</th>
                  </tr>
                </thead>

                <tbody>

                  {erpPreview.map(
                    (record, index) => (
                      <tr key={index}>

                        <td>
                          {record.documentNumber}
                        </td>

                        <td>
                          {record.erpProductCode}
                        </td>

                        <td>
                          {formatCurrency(
                            record.value
                          )}
                        </td>

                      </tr>
                    )
                  )}

                </tbody>

              </table>

            </div>

          </div>

        </div>
      )}

      {activeTab === "activeMembers" && (
        <div className="payroll-actives-section">

          <div className="payroll-detail-panel">

            <div className="payroll-actives-header">

              <div>
                <h3>Asociados Activos</h3>

                <p>
                  Cruce entre empleados y asociados activos al Fondo.
                </p>
              </div>

              <div className="payroll-actives-actions">

                <button
                  type="button"
                  className="pay-btn-primary"
                >
                  Exportar Activos
                </button>

              </div>

            </div>

          </div>

          <div className="payroll-actives-metrics">

            <div className="payroll-active-card">
              <span>Empleados Activos</span>
              <strong>
                {(activeMembersData?.summary?.activeEmployees || 0).toLocaleString("es-CO")}
              </strong>
            </div>

            <div className="payroll-active-card">
              <span>Asociados Activos</span>
              <strong>
                {(activeMembersData?.summary?.activeMembers || 0).toLocaleString("es-CO")}
              </strong>
            </div>

            <div className="payroll-active-card">
              <span>Sin Asociación</span>
              <strong>
                {(activeMembersData?.summary?.associatesNotFound || 0).toLocaleString("es-CO")}
              </strong>

            </div>

            <div className="payroll-active-card">
              <span>Por Revisar</span>
              <strong>
                {(activeMembersData?.summary?.excludedByStatus || 0).toLocaleString("es-CO")}
              </strong>
            </div>

          </div>

          <div className="payroll-detail-panel">

            <div className="payroll-results-header">

              <div>
                <h3>Resultado del Cruce</h3>

                <p>
                  Empleados encontrados tras la validación.
                </p>
              </div>

            </div>

            <div className="payroll-detail-table-container">

              {loadingActiveMembers && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  Consultando asociados activos...
                </div>
              )}

              <table className="payroll-detail-table">

                <thead>
                  <tr>
                    <th>Identificación</th>
                    <th>Nombre</th>
                    <th>Empresa</th>
                    <th>Estado Asociado</th>
                    <th>Fecha Afiliación</th>
                  </tr>
                </thead>

                <tbody>

                  {activeMembersData?.records?.map(
                    (record) => (
                      <tr
                        key={record.documentNumber}
                      >
                        <td>
                          {record.documentNumber}
                        </td>

                        <td>
                          {formatName(record.employeeName || "Sin nombre")}
                        </td>

                        <td>
                          {formatName(record.company)}
                        </td>

                        <td>
                          <span className="payroll-active-badge">
                            {
                              record.associateStatus
                            }
                          </span>
                        </td>

                        <td>
                          {record.affiliationDate
                            ? new Date(
                              record.affiliationDate
                            ).toLocaleDateString(
                              "es-CO"
                            )
                            : "-"}
                        </td>
                      </tr>
                    )
                  )}

                </tbody>

              </table>

            </div>

          </div>

        </div>
      )}

      {adjustmentModalOpen &&
        selectedAdjustment && (

          <div className="payroll-adjustment-modal-overlay">

            <div className="payroll-adjustment-modal">

              <div className="payroll-adjustment-modal-header">

                <div>

                  <h2>
                    {selectedAdjustment.employeeName}
                  </h2>

                  <span>
                    Identificación: {selectedAdjustment.documentNumber}
                  </span>

                </div>

                <button
                  type="button"
                  className="payroll-adjustment-close"
                  disabled={
                    savingAdjustment
                  }
                  onClick={() => {
                    if (!savingAdjustment) {
                      setAdjustmentModalOpen(
                        false
                      );
                    }
                  }}
                >
                  ✕
                </button>

              </div>

              <div className="payroll-adjustment-summary">

                <div className="payroll-adjustment-stat">

                  <span>Valor en Deducción</span>

                  <strong>
                    {formatCurrency(
                      selectedAdjustment.sentTotal
                    )}
                  </strong>

                </div>

                <div className="payroll-adjustment-stat">

                  <span>Valor Reportado</span>

                  <strong>
                    {formatCurrency(
                      selectedAdjustment.reportedTotal
                    )}
                  </strong>

                </div>

                <div className="payroll-adjustment-stat">

                  <span>Saldo por Distribuir</span>

                  <strong
                    className={
                      getCurrentBalance() === 0
                        ? "positive"
                        : "negative"
                    }
                  >
                    {formatCurrency(
                      getCurrentBalance()
                    )}
                  </strong>

                </div>

              </div>

              <div className="payroll-adjustment-content">

                <table
                  className="payroll-detail-table"
                >

                  <thead>

                    <tr>

                      <th>
                        Concepto
                      </th>

                      <th>
                        Deducción
                      </th>

                      <th>
                        Nuevo Valor
                      </th>

                      <th>
                        Excluir
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {adjustmentConcepts.map(
                      (concept) => (

                        <tr
                          key={
                            concept.recordId
                          }
                        >

                          <td>
                            {formatName(concept.productName)}
                          </td>

                          <td>

                            {formatCurrency(
                              concept.sentValue
                            )}

                          </td>

                          <td>

                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={
                                concept.newValue
                              }
                              disabled={
                                concept.excluded ||
                                savingAdjustment
                              }
                              onChange={(e) =>
                                updateConceptValue(
                                  concept.recordId,
                                  e.target.value
                                )
                              }
                            />

                          </td>

                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(concept.excluded)}
                              disabled={savingAdjustment}
                              style={{
                                accentColor: "#333333",
                              }}
                              onChange={() =>
                                toggleConceptExclusion(
                                  concept.recordId
                                )
                              }
                            />
                          </td>
                        </tr>

                      )
                    )}

                  </tbody>

                </table>

              </div>

              <div
                className="payroll-adjustment-modal-footer"
              >

                <button
                  type="button"
                  className="pay-btn-primary"
                  onClick={
                    saveAdjustmentPerson
                  }
                  disabled={
                    savingAdjustment
                  }
                >
                  {savingAdjustment
                    ? "Guardando..."
                    : "Guardar cambios"}
                </button>

              </div>

            </div>

          </div>

        )}

    </div>
  );
};

export default PayrollClientDetail;